import React, { useCallback, useEffect, useState } from 'react';
import { usePageMeta } from '../utils/usePageMeta';
import { trackEvent } from '../utils/analytics';
import { SubPageNav, SubPageFoot, ArrowIcon } from '../components/SubPageChrome';
import EventIntakeModal from '../components/EventIntakeModal';
import {
  EVENT_INTAKE_CONFIGS,
  EventIntakeCategory,
  EventIntakeConfig,
  getEventIntakeConfig,
} from '../constants/eventIntake';
import courtsImg from '../assets/tempo_courts1.webp';
import loungeImg from '../assets/lounge.webp';
import barImg from '../assets/tempo_bar1.webp';
import heroImg from '../assets/intro_to_padel_2.webp';
import './HomePage.css';
import './EventsPageV2.css';

const stats = [
  { value: '9', label: 'Courts' },
  { value: '250', label: 'Guest capacity' },
  { value: '1', label: 'Point of contact' },
];

const included = [
  {
    title: 'Court programming',
    body: 'Coach-led intros, open play, round robins, or structured activities for first-time and experienced players.',
  },
  {
    title: 'Food and beverage',
    body: 'Catering, snacks, beverage service, and club-managed alcohol service for compliant event operations.',
  },
  {
    title: 'Venue support',
    body: 'Indoor and outdoor areas, lounge space, AV support, event staff, and day-of coordination.',
  },
];

const EventsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<EventIntakeCategory | null>(null);

  const selectedConfig = selectedCategory ? getEventIntakeConfig(selectedCategory) : null;

  usePageMeta({
    title: 'Events at TEMPO | Corporate Events, Private Parties & Club Buyouts',
    description:
      'Host corporate events, private parties, and full club buyouts at TEMPO Padel & Pickleball Club in Houston. Court programming, lounge, bar, food, and event support available.',
    canonicalPath: '/events',
  });

  useEffect(() => {
    trackEvent('page_view', { page: 'events' });
  }, []);

  const openIntake = useCallback((config: EventIntakeConfig, placement: string) => {
    trackEvent('event_intake_open', { event_category: config.id, placement });
    setSelectedCategory(config.id);
  }, []);

  const scrollToOptions = useCallback(() => {
    document.getElementById('event-options')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="home-v2 events-v2">
      <SubPageNav ctaLabel="Plan Event" ctaHref="#event-options" onCtaClick={scrollToOptions} active="events" />

      <section className="events-hero" aria-labelledby="events-hero-title">
        <div
          className="events-hero-bg"
          role="img"
          aria-label="Guests gathering around TEMPO padel courts"
          style={{ backgroundImage: `url(${heroImg})` }}
        />
        <div className="events-hero-content v2-container">
          <div className="kicker">Events at Tempo</div>
          <h1 id="events-hero-title">
            Events at <em>Tempo.</em>
          </h1>
          <p className="lede">
            Corporate offsites, private parties, and full club buyouts built around play, food,
            drinks, and the energy of a real club.
          </p>
          <div className="events-hero-actions">
            <button type="button" className="v2-btn v2-btn-primary" onClick={scrollToOptions}>
              Start Planning
              <ArrowIcon />
            </button>
            <a className="v2-btn v2-btn-ghost" href="tel:+17135706716">
              Call the Club
            </a>
          </div>
          <div className="events-hero-stats" aria-label="Event venue highlights">
            {stats.map((stat) => (
              <div className="events-stat" key={stat.label}>
                <span className="n">{stat.value}</span>
                <span className="l">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section noborder" id="event-options" aria-labelledby="event-options-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">01 /</span>
              <span className="label">Event Type</span>
            </div>
            <h2 id="event-options-title">
              Choose your <em>format.</em>
            </h2>
          </div>

          <div className="events-card-grid">
            {EVENT_INTAKE_CONFIGS.map((config) => (
              <article className="events-type-card" key={config.id}>
                <span className="events-card-eyebrow">{config.eyebrow}</span>
                <h3>{config.cardTitle}</h3>
                <p>{config.summary}</p>
                <div className="events-card-meta">
                  <span>{config.capacity}</span>
                  <span>{config.bestFor}</span>
                </div>
                <button
                  type="button"
                  className="v2-btn v2-btn-on-light"
                  onClick={() => openIntake(config, 'event_card')}
                >
                  {config.ctaLabel}
                  <ArrowIcon />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section events-venue-section" aria-labelledby="events-venue-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">02 /</span>
              <span className="label">The Venue</span>
            </div>
            <h2 id="events-venue-title">
              Courts, lounge, bar, <em>all yours.</em>
            </h2>
          </div>

          <div className="events-gallery">
            <div className="v2-bigimg">
              <img src={courtsImg} alt="Tempo padel courts for events" />
              <div className="overlay">
                <div className="top">
                  <span>Courts</span>
                  <span>Padel + Pickleball</span>
                </div>
                <div className="bottom">
                  <div className="cap">Built around play.</div>
                </div>
              </div>
            </div>
            <div className="events-gallery-pair">
              <div className="v2-bigimg aspect-43">
                <img src={loungeImg} alt="Tempo lounge for private events" />
              </div>
              <div className="v2-bigimg aspect-43">
                <img src={barImg} alt="Tempo bar for event beverage service" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="v2-section" aria-labelledby="events-included-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">03 /</span>
              <span className="label">Planning Support</span>
            </div>
            <h2 id="events-included-title">
              Bring the people. <em>We run</em> the details.
            </h2>
          </div>

          <div className="v2-pillars events-included-grid">
            {included.map((item, index) => (
              <div className="v2-pillar" key={item.title}>
                <div className="idx">{String.fromCharCode(65 + index)}.</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="v2-section events-final-section" aria-labelledby="events-final-title">
        <div className="v2-container">
          <div className="v2-sec-head">
            <div className="left">
              <span className="num">04 /</span>
              <span className="label">Inquire</span>
            </div>
            <h2 id="events-final-title">
              Tell us what <em>you are</em> planning.
            </h2>
          </div>
          <div className="events-final-actions">
            {EVENT_INTAKE_CONFIGS.map((config) => (
              <button
                key={config.id}
                type="button"
                className="v2-slot request"
                onClick={() => openIntake(config, 'final_cta')}
              >
                <span className="d">{config.eyebrow}</span>
                <span className="t">{config.cardTitle}</span>
                <span className="s">Open intake</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <SubPageFoot />

      <div className="v2-sticky-cta">
        <button type="button" className="v2-btn v2-btn-on-light" onClick={scrollToOptions}>
          Plan an Event
          <ArrowIcon />
        </button>
      </div>

      <EventIntakeModal
        isOpen={Boolean(selectedConfig)}
        config={selectedConfig}
        onClose={() => setSelectedCategory(null)}
      />
    </div>
  );
};

export default EventsPage;
